import { Box, Grid } from '@mui/material';
import { useMemo } from 'react';
import { convertCentToFahr, convertKpaToPsi } from 'utils/helpers/unitSystem';
import { fixedLimts } from 'views/TirePressure/util';
import FourWheels from '../Schematics/FourWheels';
import TirePressure from '../TirePressure';
import { convertUtcToDisplayTime } from 'utils/helpers/dateTime';
import useTimeDisplayMode from 'hooks/useTimeDisplayMode';
import { TireStatus, determineTireStatus } from 'utils/tpms';
import { useAppSelector } from 'store/hook';
import { YEAR_MONTH_DAY_TIME_FORMAT } from 'utils/constants/dateTime';

const tireNumberSystem = ['0x17', '0x19', '0x27', '0x29'];

export const FourTires = ({
  tireData,
  limits,
  enableDarkTheme,
  unitSystem,
  batteryData,
  selectedAsset,
  tireCodes,
  metricType,
  makeModel,
  onTireClick,
}: {
  tireData: any;
  limits: any;
  enableDarkTheme: boolean;
  unitSystem: string;
  tireCodes: any;
  metricType: string;
  makeModel: string;
  batteryData: any;
  selectedAsset: any;
  onTireClick?: (tireCode: string) => void;
}) => {
  const { fleetData } = useAppSelector((state) => state.tireProfileReducer);
  const { preferredTimeDisplayMode, timezone } = useTimeDisplayMode();
  const lowerCaseMetricType = metricType.toLowerCase();
  // @ts-expect-error ignore this we have it defined
  const limitsData = fixedLimts[unitSystem][lowerCaseMetricType];

  const data = useMemo(() => {
    try {
      const isTemperature = lowerCaseMetricType === 'temperature';

      const fitmentData = tireData?.tireFitmentData ?? [];

      const latestAssetData =
        fleetData[
          lowerCaseMetricType === 'temperature' ? 'temperature' : 'pressure'
        ]?.[selectedAsset?.device?.deviceId] || {};

      const result: any = {};
      tireNumberSystem.forEach((code, index) => {
        const tireNumber = index + 1;

        const latestInfo = latestAssetData[code];
        const value = latestInfo?.value ?? null;

        const lastUpdated = latestInfo?.ts
          ? convertUtcToDisplayTime(
              new Date(latestInfo.ts).toISOString(),
              preferredTimeDisplayMode,
              timezone,
              YEAR_MONTH_DAY_TIME_FORMAT
            )
          : 'N/A';
        result[`p${tireNumber}_last_updated`] = lastUpdated;
        result[`p${tireNumber}_ts`] = latestInfo?.ts ?? null;

        // `limits` is now the pre-filtered positions array from axle-wise API.
        // Each item has tireCode directly — just match by tireCode.
        let tireLimit =
          limits?.find((item: any) => item.tireCode === code) || {};

        // incase of temp use value from config, else use it from be,
        // also incase of imperial we need to convert as values are saved on be in metric always
        const maxValue = isTemperature
          ? unitSystem === 'metric'
            ? tireLimit.maxTemperature
            : convertCentToFahr(tireLimit.maxTemperature)
          : unitSystem === 'metric'
          ? tireLimit.maxPressure
          : convertKpaToPsi(tireLimit?.maxPressure);
        const minValue = isTemperature
          ? unitSystem === 'metric'
            ? tireLimit.minTemperature
            : convertCentToFahr(tireLimit.minTemperature)
          : unitSystem === 'metric'
          ? tireLimit.minPressure
          : convertKpaToPsi(tireLimit?.minPressure);
        const warningValue = isTemperature
          ? unitSystem === 'metric'
            ? tireLimit.warningTemperature
            : convertCentToFahr(tireLimit.warningTemperature)
          : unitSystem === 'metric'
          ? tireLimit.warningPressure
          : convertKpaToPsi(tireLimit?.warningPressure);

        const fitmentInfo = fitmentData.find(
          (item: any) => item.tireCode === code
        );

        const fitmentDate = fitmentInfo?.installedAt
          ? convertUtcToDisplayTime(
              fitmentInfo.installedAt,
              preferredTimeDisplayMode,
              timezone,
              'DD MMM YYYY'
            )
          : 'Not Set';

        tireLimit = {
          ...tireLimit,
          minValue,
          maxValue,
        };

        result[`p${tireNumber}`] = value;
        result[`p${tireNumber}Limits`] = tireLimit;
        result[`p${tireNumber}_fitment_date`] = fitmentDate;
        result[`p${tireNumber}_serial_number`] =
          fitmentInfo?.serialNumber || null;
        result[`${tireNumber}HasSensor`] = fitmentInfo ?? false;

        // Calculate Tire Status using determineTireStatus
        const status = determineTireStatus(
          value,
          { min: minValue, max: maxValue, warning: warningValue },
          !!fitmentInfo,
          lowerCaseMetricType as 'pressure' | 'temperature'
        );
        result[`p${tireNumber}Status`] = status;

        // Keep isOverMax for backward compatibility
        const isOverMaxRes = status === TireStatus.UNHEALTHY;
        result[`isP${tireNumber}WarninigMax`] = isOverMaxRes;
      });

      result.tireStatusMap = {
        p1: result.p1Status,
        p2: result.p2Status,
        p3: result.p3Status,
        p4: result.p4Status,
      };

      // Calculate per-tire staleness (24 hours threshold)
      const checkIsStale = (ts: number | null | undefined) => {
        if (!ts) return false;
        const hoursDiff = (Date.now() - ts) / (1000 * 60 * 60);
        return hoursDiff > 24;
      };

      result.tireStaleMap = {
        p1: checkIsStale(result.p1_ts),
        p2: checkIsStale(result.p2_ts),
        p3: checkIsStale(result.p3_ts),
        p4: checkIsStale(result.p4_ts),
      };

      return result;
    } catch (error) {
      console.log(error);
      return {};
    }
  }, [
    limits,
    lowerCaseMetricType,
    makeModel,
    tireData,
    unitSystem,
    fleetData,
    selectedAsset,
  ]);

  const slowLeaks = tireData.tireCurrentStatus?.slowLeaks ?? [];
  const fastLeaks = tireData.tireCurrentStatus?.fastLeaks ?? [];

  // Get battery data for this asset
  const deviceBattery = selectedAsset?.device?.deviceId
    ? batteryData?.[selectedAsset.device.deviceId]
    : undefined;

  // Map tire codes to battery status
  const getBatteryStatus = (tireCode: string) => {
    return deviceBattery?.[tireCode]?.isBatteryLow || false;
  };

  return (
    <>
      <Grid item lg={3}>
        <Box
          sx={{
            marginTop: '80px',
          }}
        >
          {[1, 3].map((item, index) => {
            return (
              <TirePressure
                key={index}
                enableDarkTheme={enableDarkTheme}
                value={data[`p${item}`]}
                limit={data[`p${item}Limits`]}
                isAlert={data[`isP${item}WarninigMax`]}
                tireCode={tireNumberSystem[item - 1]}
                unitSystem={unitSystem}
                tireCodes={tireCodes}
                metricType={metricType}
                slowLeaks={slowLeaks}
                fastLeaks={fastLeaks}
                fitmentDate={data[`p${item}_fitment_date`]}
                hasSensor={data[`${item}HasSensor`]}
                isBatteryLow={getBatteryStatus(tireNumberSystem[item - 1])}
                serialNumber={data[`p${item}_serial_number`]}
                lastUpdated={data[`p${item}_last_updated`]}
              />
            );
          })}
        </Box>
      </Grid>
      <Grid item lg={6}>
        <Box
          sx={{
            textAlign: 'center',
            paddingTop: '15px',
            paddingBottom: '30px',
          }}
        >
          <FourWheels
            name="tireVehicleSchematicIcon"
            values={data}
            tireStatusMap={data.tireStatusMap}
            tireStaleMap={data.tireStaleMap}
            onTireClick={onTireClick}
            tireNumberSystem={tireNumberSystem}
          />
        </Box>
      </Grid>
      <Grid item lg={3}>
        <Box
          sx={{
            marginTop: '80px',
          }}
        >
          {[2, 4].map((item, index) => {
            return (
              <TirePressure
                key={index}
                enableDarkTheme={enableDarkTheme}
                isAlert={data[`isP${item}WarninigMax`]}
                value={data[`p${item}`]}
                limit={data[`p${item}Limits`]}
                tireCode={tireNumberSystem[item - 1]}
                unitSystem={unitSystem}
                tireCodes={tireCodes}
                metricType={metricType}
                slowLeaks={slowLeaks}
                fastLeaks={fastLeaks}
                fitmentDate={data[`p${item}_fitment_date`]}
                hasSensor={data[`${item}HasSensor`]}
                isBatteryLow={getBatteryStatus(tireNumberSystem[item - 1])}
                serialNumber={data[`p${item}_serial_number`]}
                lastUpdated={data[`p${item}_last_updated`]}
              />
            );
          })}
        </Box>
      </Grid>
    </>
  );
};
